/**
 * HostedAgentConnectionTab — hosted-agent selection panel.
 *
 * Rendered in the Parameters (Configure) tab for FoundryHostedAgent nodes.
 * Shows a "Foundry Hosted Agent" dropdown whose options are each hosted agent
 * from hostedAgentConnections, a manage deep-link beside it that opens the
 * selected agent's detail tab, plus a "Create Hosted Agent" link (Azure-portal
 * "Create new" style) below the dropdown that deep-links to the app's Agents tab
 * with the create form open.
 *
 * "Create Hosted Agent" opens the standalone Agents page in a SEPARATE browser tab
 * (with the create wizard showing) so the author keeps their workflow open. The two
 * tabs have independent React Query caches, so a newly created agent surfaces here
 * when the dropdown is reopened (onOpenChange refetches the list) — no cross-tab
 * broadcast/nudge machinery.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Spinner,
  mergeClasses,
  Dropdown,
  Option,
  Field,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  MessageBarActions,
  Link,
} from '@coreai/fluentui-react';
import {
  CheckmarkCircle16Filled,
  Open16Regular,
  ErrorCircle16Filled,
  Warning16Filled,
} from '@fluentui/react-icons';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useAppInfoForConnections, useHostedAgents } from '@/hooks/useHostedAgent';
import { useAppStore } from '@/stores/appStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { hostedAgentDeletionKey, useHostedAgentDeletionStore } from '@/stores/hostedAgentDeletionStore';
import { useTelemetry } from '@/hooks/useTelemetry';
import { paths } from '@/routes';
import { mergeSearch } from '@/utils/primitives/mergeSearch';
import { extractConnectionReferences } from '@/services/connectionService';
import type { HostedAgentStatus, HostedAgentConnection } from '@/types/hostedAgent';
import type { ConnectionsConfiguration } from '@/types/logicApps';

/** Short status labels for the selected-agent card subtitle. */
const STATUS_LABELS: Record<HostedAgentStatus, string> = {
  connected: 'Connected',
  provisioning: 'Provisioning',
  auth_failed: 'Authentication failed',
  agent_error: 'Agent error',
  version_mismatch: 'Update available',
  unreachable: 'Unreachable',
  unknown: 'Status unknown',
};

/**
 * Build a new-tab URL for a designer hand-off (Create new / View details). Uses `mergeSearch`
 * so DURABLE params on the current URL survive into the brand-new tab's initial request —
 * notably `?env=staging|canary` (AFD routing + environment detection; QueryParamPreserver can't
 * repair a cold tab load) and `?features=…` (flag gating; hosted agents is often enabled this
 * way, and without it the new tab boots with the flag OFF and redirects to Home). One-shot
 * params are dropped by mergeSearch; `extra` carries the hand-off params (create/from/handoff).
 */
function buildHandoffUrl(path: string, extra: string): string {
  return `${path}${mergeSearch(window.location.search, extra)}`;
}

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    flex: 1,
    minHeight: 0,
    // Separate this selector block from the ParameterList fields (e.g. "System message")
    // rendered directly below in the Parameters tab, so "Create New" isn't crowded against
    // the next field label.
    marginBottom: tokens.spacingVerticalL,
  },

  selectField: {
    marginTop: tokens.spacingVerticalXS,
  },

  // Row holding the dropdown + the "open selected agent" deep-link so the manage
  // affordance sits directly beside the combobox (with the create link below).
  selectRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  selectDropdown: {
    flex: 1,
    minWidth: 0,
  },
  manageButton: {
    flexShrink: 0,
  },

  provisioningNote: {
    marginTop: tokens.spacingVerticalS,
  },

  createButton: {
    marginTop: tokens.spacingVerticalXS,
    alignSelf: 'flex-start',
    // Azure-portal "Create new" affordance: a plain underlined text link directly
    // below the dropdown (no icon), rather than a bordered/icon button.
    textDecorationLine: 'underline',
  },

  agentOption: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },

  agentOptionText: {
    display: 'flex',
    flexDirection: 'column',
  },

  statusIcon: {
    fontSize: tokens.fontSizeBase400,
    flexShrink: 0,
  },
  statusIconConnected: {
    color: tokens.colorPaletteGreenForeground1,
  },
  statusIconNone: {
    color: tokens.colorNeutralForeground3,
  },
  statusIconError: {
    color: tokens.colorPaletteRedForeground1,
  },
  statusIconWarning: {
    color: tokens.colorPaletteMarigoldForeground1,
  },

  // Agent option text — shared by the dropdown option rows.
  agentItemName: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  agentItemModel: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

// =============================================================================
// COMPONENT
// =============================================================================

export const HostedAgentConnectionTab: React.FC = () => {
  const styles = useStyles();
  // "Create or Manage" and "Finish setup" open the standalone Agents page/detail in a
  // separate browser tab (see openAgentsTab / handleOpenDetails), so the author keeps
  // this workflow open while working with the agent next door.
  // Agents live on an app-scoped route (/location/.../projects/.../apps/.../agents),
  // so the tab URL must be built from the current app context — a bare "/agents"
  // matches no route and dead-ends (issue #2529).
  const { locationName, projectName, appName, tenantId: tenantIdParam } = useParams<{ locationName: string; projectName: string; appName: string; tenantId: string }>();
  // Prefer the tenantId URL param over the store. On first render TenantLayout may
  // not have synced selectedTenantId yet; relying on the store alone can drop the
  // /tenant/:tenantId prefix and navigate the user out of tenant scope (PR #3019 review).
  const selectedTenantId = useAppStore((s) => s.selectedTenantId);
  const tenantId = tenantIdParam ?? selectedTenantId;

  const { trackEvent } = useTelemetry();
  const { agents, hostedAgentRecords, isLoading, isFetching, error, refetch } = useHostedAgents();
  const cleanupFailures = useHostedAgentDeletionStore((s) => s.cleanupFailures);
  const appId = useAppInfoForConnections()?.appId;

  // Keep connectionStore's serialization baseline in sync with every successful hosted-agent
  // fetch (including a cross-tab refetch after an agent is created in the Agents tab). Without
  // this, exportToConnectionsConfig would serialize the stale load-time snapshot on the next
  // workflow save and delete the just-created agent. Tied to the fetch — not selection — so a
  // save between the automatic refetch and a user selection still writes the current set.
  useEffect(() => {
    if (hostedAgentRecords !== undefined) {
      useConnectionStore.getState().setHostedAgentConnectionRecords(hostedAgentRecords);
    }
  }, [hostedAgentRecords]);

  const parameterValues = useNodeEditorStore((s) => s.parameterValues);
  const setParameter = useNodeEditorStore((s) => s.setParameter);
  // Read-only editors (e.g. viewing a published workflow) must not mutate node/connection
  // state. setParameter already no-ops, but the selection handler also registers a connection
  // reference (marks connections dirty) — so guard that and disable the interactive controls.
  const isReadOnly = useNodeEditorStore((s) => s.isReadOnly);

  // Cross-tab safety net against a stale save baseline. An agent created in the separate
  // Agents tab is written to the SAME connections.json this workflow serializes back on save.
  // The baseline seeded above (originalConnectionsConfig.hostedAgentConnections) only refreshes
  // when the hosted-agent list is (re)fetched — on mount and on dropdown open. If the author
  // returns to this tab after a cross-tab create WITHOUT reopening the dropdown and then saves,
  // exportToConnectionsConfig would serialize the stale baseline and drop the just-created
  // record from connections.json. Refetching when this tab regains focus/visibility keeps the
  // baseline current before any save. This is a lightweight standard refresh (not the removed
  // broadcast/hand-off machinery) and is skipped in read-only mode, which never saves.
  useEffect(() => {
    if (isReadOnly) return;
    const refresh = () => { void refetch(); };
    const onVisibility = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isReadOnly, refetch]);

  // Current selected agent name. The durable key is _modelConnection_model1
  // (persisted via modelConfigurations.model1.referenceName); hostedAgentName is
  // deleted on serialize for FoundryHostedAgent, so it's only present during an
  // in-memory session and gone after save+reopen.
  const selectedAgentName = (parameterValues['_modelConnection_model1']
    ?? parameterValues['hostedAgentName']
    ?? parameterValues['parameters.hostedAgentName']
    ?? '') as string;

  const selectedAgent = useMemo(
    () => agents.find((a) => a.key === selectedAgentName),
    [agents, selectedAgentName],
  );

  // Register a hosted agent's connection reference in THIS tab's connection store so the
  // canvas badge resolves. Creation may have happened in a separate tab (separate React
  // Query cache + connection store), so selecting a cross-tab agent would otherwise read
  // as "connection reference not found" until a full workflow reload.
  const registerAgentReference = useCallback(
    (agentKey: string) => {
      const agent = agents.find((a) => a.key === agentKey);
      if (!agent) return;
      const ref = extractConnectionReferences({
        hostedAgentConnections: { [agentKey]: agent as unknown as HostedAgentConnection },
      } as ConnectionsConfiguration)[agentKey];
      if (ref) useConnectionStore.getState().addConnectionReference(agentKey, ref);
    },
    [agents],
  );

  const handleSelectAgent = useCallback(
    (agentKey: string) => {
      if (isReadOnly) return;
      setParameter('hostedAgentName', agentKey);
      // Set the modelConfigurations reference for runtime connection resolution
      setParameter('_modelConnection_model1', agentKey);
      registerAgentReference(agentKey);
    },
    [setParameter, registerAgentReference, isReadOnly],
  );

  // Open the selected agent's detail/setup view in a NEW tab. That page runs the
  // provisioning pipeline (verify → identities → RBAC → readiness) and flips the agent
  // from "provisioning" to "connected"; keeping it in a separate tab lets the author keep
  // authoring here. Preserves the #2529 app-scoped, tenant-aware route.
  const handleOpenDetails = useCallback(() => {
    if (!selectedAgent || !locationName || !projectName || !appName) return;
    const url = buildHandoffUrl(
      paths.appAgentDetail(locationName, projectName, appName, selectedAgent.configuration.agentName, tenantId),
      '?from=designer',
    );
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [selectedAgent, locationName, projectName, appName, tenantId]);

  // When the currently selected agent's status changes underneath us, keep it fresh via the
  // dropdown's refetch-on-open (see the Dropdown's onOpenChange) and the tab focus/visibility
  // refetch above (the stale-baseline safety net). No broadcast-driven refetch: opening the
  // dropdown or returning to this tab pulls the latest list from connections.json.

  const openAgentsTab = useCallback(() => {
    // Don't offer creation while the hosted-agent list is unavailable OR mid-refresh. isFetching
    // (not isLoading) is the correct signal: the focus/visibility effect fires a background
    // refetch() during which isLoading stays false but the list is momentarily stale — returning
    // from the Agents tab right after creating an agent would otherwise let the author open a
    // second create wizard before the new agent is visible, defeating the duplicate-creation
    // safeguard. Mirror the standalone Agents page and no-op here (the Link is also disabled).
    if (isReadOnly || isFetching || error || !locationName || !projectName || !appName) return;
    trackEvent('agent.tab.opened_in_new_tab', { source: 'hostedAgentConnectionTab' });
    // Open the standalone Agents page (the app's Agents tab) in a SEPARATE browser tab with the
    // create form (wizard) already showing — ?create=1 seeds AgentsPage's wizardOpen so the author
    // lands on the create form (where subscription/model/etc. are captured), not just the list.
    // ?from=designer drives the "return to your workflow" guidance; buildHandoffUrl preserves
    // durable params (env, features) for the cold new-tab load.
    const url = buildHandoffUrl(
      paths.appAgents(locationName, projectName, appName, tenantId),
      '?create=1&from=designer',
    );
    // noopener/noreferrer: the new tab must not be able to navigate this workflow tab (reverse
    // tabnabbing). It's user-initiated so isn't popup-blocked in practice.
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [isReadOnly, isFetching, error, locationName, projectName, appName, tenantId, trackEvent]);

  // Pick a distinct glyph per status so a row is scannable at a glance
  // (checkmark = healthy, spinner = provisioning, error = down/auth-failed,
  // warning = update available) instead of a recolored checkmark that always
  // reads as "OK". Shared by the dropdown option rows and the selected-agent card.
  const renderStatusGlyph = (status: HostedAgentStatus): React.ReactNode => {
    switch (status) {
      case 'connected':
        return <CheckmarkCircle16Filled aria-hidden="true" className={mergeClasses(styles.statusIcon, styles.statusIconConnected)} />;
      case 'provisioning':
        return <Spinner size="tiny" aria-hidden className={styles.statusIcon} />;
      case 'auth_failed':
      case 'agent_error':
      case 'unreachable':
        return <ErrorCircle16Filled aria-hidden="true" className={mergeClasses(styles.statusIcon, styles.statusIconError)} />;
      case 'version_mismatch':
        return <Warning16Filled aria-hidden="true" className={mergeClasses(styles.statusIcon, styles.statusIconWarning)} />;
      default:
        return <Warning16Filled aria-hidden="true" className={mergeClasses(styles.statusIcon, styles.statusIconNone)} />;
    }
  };

  // The provisioning note is the only remaining consumer of the selected agent's live status
  // (the status card was removed). Show it only when the currently selected agent is still
  // running its setup pipeline.
  const selectedAgentIsProvisioning = selectedAgent?.status === 'provisioning';

  return (
    <div className={styles.container} data-testid="hosted-agent-connection-tab">
      {/* Provisioning note: a freshly created agent stays in "provisioning" until its setup
          pipeline runs. Offer to finish setup in an in-context side drawer so the user can
          return to this workflow when it's done. */}
      {selectedAgentIsProvisioning && (
        <MessageBar intent="info" role="status" className={styles.provisioningNote} data-testid="hosted-agent-provisioning-note">
          <MessageBarBody>
            This agent is still provisioning — it needs a few minutes to finish setup before it can run.{' '}
            <Link as="button" onClick={handleOpenDetails}>Open its setup in a new tab</Link>; your workflow stays open.
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Load failure: fetchConnectionsJson now rejects a real 401/403/5xx/network error instead of
          masking it as an empty list, so surface it here. Without this the dropdown would just show
          "No hosted agents found" and the author might create a duplicate of an agent that only
          failed to load. Persists until a successful refetch clears `error`. */}
      {error && (
        <MessageBar intent="error" layout="multiline" role="alert" data-testid="hosted-agent-load-error">
          <MessageBarBody>
            <MessageBarTitle>Couldn&apos;t load hosted agents</MessageBarTitle>
            &nbsp;The list failed to load, so it may be incomplete. Check your connection and retry.
          </MessageBarBody>
          <MessageBarActions>
            <Button size="small" onClick={() => void refetch()} disabled={isLoading}>Retry</Button>
          </MessageBarActions>
        </MessageBar>
      )}

      {/* Select Hosted Agent — dropdown of existing agents only. Opening it refetches so a
          just-created agent (from the other tab) is always listed. Creation is a separate
          link directly below (a11y: a combobox option must not trigger navigation / open a new tab). */}
      <Field label="Foundry Hosted Agent" className={styles.selectField}>
        <div className={styles.selectRow}>
          <Dropdown
            className={styles.selectDropdown}
            aria-label="Select hosted agent"
            data-testid="hosted-agent-dropdown"
            disabled={isReadOnly}
            placeholder={isLoading ? 'Loading hosted agents…' : 'Select a hosted agent'}
            value={selectedAgent?.displayName ?? ''}
            selectedOptions={selectedAgent ? [selectedAgent.key] : []}
            onOpenChange={(_, data) => { if (data.open) void refetch(); }}
            onOptionSelect={(_, data) => {
              if (data.optionValue) {
                handleSelectAgent(data.optionValue);
              }
            }}
          >
            {!isLoading && !error && agents.length === 0 && (
              <Option key="no-agents" value="__no_agents__" text="No hosted agents found" disabled>
                No hosted agents found
              </Option>
            )}
            {agents.map((agent) => {
              const hasCleanupFailure = !!cleanupFailures[hostedAgentDeletionKey(appId, agent.key)];
              const statusLabel = hasCleanupFailure ? 'Cleanup pending' : STATUS_LABELS[agent.status];
              return (
                <Option
                  key={agent.key}
                  value={agent.key}
                  text={`${agent.displayName}, ${statusLabel}`}
                >
                  <span className={styles.agentOption}>
                    {hasCleanupFailure
                      ? <ErrorCircle16Filled aria-hidden="true" className={mergeClasses(styles.statusIcon, styles.statusIconError)} />
                      : renderStatusGlyph(agent.status)}
                    <span className={styles.agentOptionText}>
                      <Text className={styles.agentItemName}>{agent.displayName}</Text>
                      <Text className={styles.agentItemModel}>{agent.configuration.modelDeployment} · {statusLabel}</Text>
                    </span>
                  </span>
                </Option>
              );
            })}
          </Dropdown>
          {/* Manage deep-link: opens the SELECTED agent's detail/setup view in a new tab so the
              author can manage that specific agent while keeping this workflow open. Disabled
              until an agent is selected (nothing to open otherwise). */}
          <Button
            appearance="subtle"
            size="small"
            icon={<Open16Regular />}
            onClick={handleOpenDetails}
            disabled={isReadOnly || !selectedAgent}
            className={styles.manageButton}
            data-testid="hosted-agent-manage-button"
            aria-label="Open the selected hosted agent in a new tab"
            title="Open the selected hosted agent"
          />
        </div>
        <Link
          onClick={() => openAgentsTab()}
          disabled={isReadOnly || isFetching || !!error}
          className={styles.createButton}
          data-testid="hosted-agent-create-button"
          aria-label="Create a hosted agent (opens the Agents tab create form in a new tab)"
        >
          Create New
        </Link>
      </Field>

    </div>
  );
};
